import { Circle, FabricText, Group, Line, Polyline, Rect } from 'fabric';
import type { FabricObject } from 'fabric';
import { applyFill } from '../fill';
import { registerRecipe } from '../registry';
import { tagSmartObject } from '../types';
import type {
  ContentSourceSpec,
  FillValue,
  StandardContentPayload,
  ToolRecipe,
} from '../types';

/**
 * Labeled callouts — numbered pins + leader lines + docked label boxes,
 * rendered as a transparent overlay sized to sit exactly over an image or
 * diagram region. AI-generated artwork garbles any text baked into it, so
 * the labels live here as a parametric vector widget layered ON TOP.
 *
 * Children are built in ABSOLUTE coordinates inside the region, then grouped
 * and re-anchored at 0,0. A transparent bounds Rect pins the group to exactly
 * width × height (confetti-scatter's trick), so placement over the underlying
 * art stays stable across regenerations. Params arrive untrusted (LLM- or
 * user-typed), so every read goes through a coercing helper and generate()
 * never throws.
 */

const INK = '#1e293b';

// ---------------------------------------------------------------------------
// Defensive param coercion — params may be numbers-as-strings, JSON strings,
// or missing entirely; the recipe must render something sensible regardless.
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : (v as number);
  return typeof n === 'number' && Number.isFinite(n) ? n : fallback;
}

function str(v: unknown, fallback: string): string {
  return typeof v === 'string' && v.trim() !== '' ? v : fallback;
}

/** Like str(), but an explicit empty string survives ("" = feature off). */
function strOrEmpty(v: unknown, fallback: string): string {
  return typeof v === 'string' ? v : fallback;
}

function bool(v: unknown, fallback: boolean): boolean {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return fallback;
}

/** json params may arrive as a JSON string or an already-parsed array. */
function jsonArray<T>(v: unknown, fallback: T[]): T[] {
  let parsed: unknown = v;
  if (typeof v === 'string') {
    try {
      parsed = JSON.parse(v);
    } catch {
      return fallback;
    }
  }
  return Array.isArray(parsed) ? (parsed as T[]) : fallback;
}

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

// ---------------------------------------------------------------------------
// Text helper — measure-and-fit so long labels never escape the region.
// ---------------------------------------------------------------------------

/**
 * Build a left/top-anchored text whose rendered width fits maxW: measure at
 * the requested size first, then rebuild proportionally smaller if needed
 * (FabricText measures itself at construction).
 */
function fitText(
  content: string,
  size: number,
  weight: string,
  font: string,
  fill: string,
  maxW: number,
): FabricText {
  const make = (fontSize: number) =>
    new FabricText(content, {
      left: 0,
      top: 0,
      fontSize,
      fontFamily: font,
      fontWeight: weight,
      fill,
      originX: 'left',
      originY: 'top',
    });
  const text = make(size);
  if (text.width > maxW && text.width > 0) {
    return make(Math.max(8, Math.floor(size * (maxW / text.width))));
  }
  return text;
}

// ---------------------------------------------------------------------------
// callout-labels
// ---------------------------------------------------------------------------

interface CalloutItemSpec {
  pinX?: unknown;
  pinY?: unknown;
  label?: unknown;
  text?: unknown;
  side?: unknown;
}

const SAMPLE_ITEMS: CalloutItemSpec[] = [
  { pinX: 58, pinY: 20, label: 'Crater rim', text: 'Raised outer edge' },
  { pinX: 46, pinY: 52, label: 'Central peak', text: 'Rebound uplift' },
  { pinX: 34, pinY: 80, label: 'Ejecta blanket', text: 'Scattered debris' },
];

/** One resolved callout, carried between the layout passes below. */
interface PlacedCallout {
  index: number;
  pinX: number;
  pinY: number;
  side: 'left' | 'right';
  labelText: FabricText;
  subText: FabricText | null;
  boxW: number;
  boxH: number;
  top: number; // desired, then resolved by the stacking passes
}

// ---------------------------------------------------------------------------
// Smart-fill seam — PARTIAL fill: vocabulary can supply the WORDS, but never
// the teacher's diagram, so pin positions are never touched.
// ---------------------------------------------------------------------------

/** The label box is small — keep the optional detail line short. */
const MAX_ITEM_TEXT_LEN = 70;

/** Trim to maxLen without cutting mid-word; append "..." only when cut. */
function truncateForLabel(text: string, maxLen: number): string {
  const t = text.replace(/\s+/g, ' ').trim();
  if (t.length <= maxLen) return t;
  const cut = t.slice(0, maxLen);
  const lastSpace = cut.lastIndexOf(' ');
  const clipped = lastSpace > maxLen * 0.6 ? cut.slice(0, lastSpace) : cut;
  return `${clipped.trim()}...`;
}

const contentSource: ContentSourceSpec = {
  kind: 'standard',
  wants: ['vocabulary'],
  applyContent(payload: StandardContentPayload, current: Record<string, unknown>) {
    // Merge into a COPY of current so every visual param (width/height, pin
    // style, colors, fonts, panel style) is untouched.
    const next = { ...current };
    const vocab = Array.isArray(payload?.vocabulary) ? payload.vocabulary : [];
    if (vocab.length) {
      // HONEST PARTIAL FILL: the graph has no idea where the teacher's
      // diagram puts its parts, so pinX/pinY/side are read from the CURRENT
      // items and copied through untouched — only label/text are swapped in,
      // for at most as many items as already exist (jsonArray mirrors
      // generate()'s own string-or-array coercion + SAMPLE_ITEMS fallback,
      // so a blank draft still gets 3 real pin positions to attach words
      // to). A vocab list longer than the item count does NOT grow the
      // array (no invented positions); a shorter one leaves the untouched
      // tail exactly as it was.
      const currentItems = jsonArray<CalloutItemSpec>(current.items, SAMPLE_ITEMS);
      next.items = currentItems.map((item, i) => {
        const v = vocab[i];
        if (!v) return item;
        return {
          ...item,
          label: v.term ?? '',
          text: v.definition ? truncateForLabel(v.definition, MAX_ITEM_TEXT_LEN) : '',
        };
      });
    }
    // No title/heading field on this recipe to gate (it's a pin overlay).
    // Provenance (survives regenerate — declared schema fields below).
    if (payload?.standard?.code) next.sourceStandardCode = payload.standard.code;
    if (payload?.standard?.id) next.sourceStandardId = payload.standard.id;
    return next;
  },
};

const calloutLabels: ToolRecipe = {
  id: 'callout-labels',
  domain: 'science',
  tags: ['labels', 'diagram', 'callout', 'leader-line', 'anatomy', 'parts'],
  featured: true,
  name: 'Callout Labels',
  category: 'decor',
  version: 1,
  description:
    'Transparent overlay of numbered pins, elbow leader lines and edge-docked label boxes — for labeling parts of an image or diagram layered underneath.',
  defaults: {
    width: 800,
    height: 500,
    items: SAMPLE_ITEMS.map((it) => ({ ...it })),
    numbered: true,
    pinRadius: 16,
    pinFill: '#e11d48',
    pinTextColor: '#ffffff',
    lineColor: INK,
    lineWidth: 4,
    labelFontSize: 26,
    fontFamily: 'Arial',
    textColor: INK,
    panelFill: '#ffffff',
    panelStroke: INK,
    panelPadding: 12,
    sourceStandardCode: '',
    sourceStandardId: '',
  },
  schema: [
    {
      key: 'items',
      label: 'Callouts',
      type: 'list',
      help: 'Each callout is a pin on the artwork plus a labeled box at the edge.',
      summaryKeys: ['label', 'text'],
      minItems: 1,
      maxItems: 12,
      itemSchema: [
        { key: 'label', label: 'Label', type: 'text' },
        { key: 'text', label: 'Detail line', type: 'text', help: 'Optional smaller second line.' },
        {
          key: 'pinX',
          label: 'Pin X (%)',
          type: 'number',
          min: 0,
          max: 100,
          step: 1,
          help: '0 = left edge of the region, 100 = right edge.',
        },
        {
          key: 'pinY',
          label: 'Pin Y (%)',
          type: 'number',
          min: 0,
          max: 100,
          step: 1,
          help: '0 = top edge of the region, 100 = bottom edge.',
        },
        {
          key: 'side',
          label: 'Box side',
          type: 'select',
          options: [
            { value: 'auto', label: 'Auto (nearest edge)' },
            { value: 'left', label: 'Left' },
            { value: 'right', label: 'Right' },
          ],
        },
      ],
    },
    { key: 'width', label: 'Region width (px)', type: 'number', min: 200, max: 4000, step: 10 },
    { key: 'height', label: 'Region height (px)', type: 'number', min: 150, max: 4000, step: 10 },
    { key: 'numbered', label: 'Numbered pins', type: 'boolean', help: 'Off = small plain dots.' },
    { key: 'pinFill', label: 'Pin color', type: 'fill', help: 'Solid, gradient, or texture.' },
    { key: 'lineColor', label: 'Leader line color', type: 'color' },
    { key: 'fontFamily', label: 'Font', type: 'font' },
    { key: 'textColor', label: 'Label text color', type: 'fill', help: 'Solid, gradient, or texture.' },
    {
      key: 'panelFill',
      label: 'Label box fill',
      type: 'fill',
      help: 'Leave empty for bare text (no box). Solid, gradient, or texture.',
    },
    {
      key: 'pinRadius',
      label: 'Pin radius (px)',
      type: 'number',
      min: 6,
      max: 60,
      step: 1,
      advanced: true,
    },
    {
      key: 'pinTextColor',
      label: 'Pin number color',
      type: 'fill',
      help: 'Solid, gradient, or texture.',
      advanced: true,
    },
    {
      key: 'lineWidth',
      label: 'Leader line width',
      type: 'number',
      min: 1,
      max: 16,
      step: 1,
      advanced: true,
    },
    {
      key: 'labelFontSize',
      label: 'Label size',
      type: 'number',
      min: 10,
      max: 80,
      step: 1,
      advanced: true,
    },
    {
      key: 'panelStroke',
      label: 'Label box outline',
      type: 'color',
      help: 'Leave empty for no outline.',
      advanced: true,
    },
    {
      key: 'panelPadding',
      label: 'Box padding (px)',
      type: 'number',
      min: 0,
      max: 60,
      step: 1,
      advanced: true,
    },
    {
      key: 'sourceStandardCode',
      label: 'Source standard code',
      type: 'text',
      advanced: true,
      help: 'Set automatically when filled from a standard.',
    },
    {
      key: 'sourceStandardId',
      label: 'Source standard id',
      type: 'text',
      advanced: true,
      help: 'Set automatically when filled from a standard.',
    },
  ],
  contentSource,
  async generate(params) {
    const width = clamp(num(params.width, 800), 120, 5000);
    const height = clamp(num(params.height, 500), 80, 5000);
    const numbered = bool(params.numbered, true);
    const pinRadius = clamp(num(params.pinRadius, 16), 4, 100);
    // Fills upgraded to FillValue: a solid string keeps the str()/strOrEmpty
    // coercion (back-compat — byte-identical), an object becomes a gradient /
    // texture. pin fill, label-box fill, label text and pin-number text all
    // land on fabric `fill`. lineColor / panelStroke stay plain colors — they
    // land on strokes only (house Border law).
    const pinFill: FillValue =
      typeof params.pinFill === 'string' || params.pinFill == null
        ? str(params.pinFill, '#e11d48')
        : (params.pinFill as FillValue);
    const pinTextFill: FillValue =
      typeof params.pinTextColor === 'string' || params.pinTextColor == null
        ? str(params.pinTextColor, '#ffffff')
        : (params.pinTextColor as FillValue);
    const lineColor = str(params.lineColor, INK);
    const lineWidth = clamp(num(params.lineWidth, 4), 1, 20);
    const labelSize = clamp(num(params.labelFontSize, 26), 8, 120);
    const font = str(params.fontFamily, 'Arial');
    const textFill: FillValue =
      typeof params.textColor === 'string' || params.textColor == null
        ? str(params.textColor, INK)
        : (params.textColor as FillValue);
    const panelFill: FillValue =
      typeof params.panelFill === 'string' || params.panelFill == null
        ? strOrEmpty(params.panelFill, '#ffffff')
        : (params.panelFill as FillValue);
    const panelStroke = strOrEmpty(params.panelStroke, INK);
    const pad = clamp(num(params.panelPadding, 12), 0, 80);
    const subSize = labelSize * 0.72;

    // Junk items (non-objects, unparseable pin coords) are dropped; an empty
    // overlay is useless on a poster, so fall back to the sample set.
    let specs = jsonArray<CalloutItemSpec>(params.items, SAMPLE_ITEMS)
      .filter((it): it is CalloutItemSpec => Boolean(it) && typeof it === 'object')
      .filter((it) => Number.isFinite(num(it.pinX, NaN)) && Number.isFinite(num(it.pinY, NaN)))
      .slice(0, 12);
    if (specs.length === 0) specs = SAMPLE_ITEMS;

    // Plain dots read better smaller than numbered badges; either way the pin
    // (plus its white ring) must stay inside the region so the bounds hold.
    const ringW = clamp(pinRadius * 0.2, 2, 5);
    const dotR = numbered ? pinRadius : Math.max(4, pinRadius * 0.5);
    const pinPad = dotR + ringW / 2;
    // Boxes may claim at most ~40% of the region width so pins keep room.
    const maxTextW = Math.max(40, width * 0.4 - pad * 2);

    // Pass 1 — resolve pins, sides, and measured box geometry.
    const placed: PlacedCallout[] = specs.map((it, i) => {
      const pinX = clamp((clamp(num(it.pinX, 50), 0, 100) / 100) * width, pinPad, width - pinPad);
      const pinY = clamp((clamp(num(it.pinY, 50), 0, 100) / 100) * height, pinPad, height - pinPad);
      const sideRaw = str(it.side, 'auto');
      const side: 'left' | 'right' =
        sideRaw === 'left' || sideRaw === 'right' ? sideRaw : pinX < width / 2 ? 'left' : 'right';

      // fitText needs a string fill; the real textFill (which may be a gradient/
      // pattern) is applied after layout. Fill never affects text metrics, so
      // this placeholder does not change wrapping/sizing.
      const labelText = fitText(str(it.label, `Label ${i + 1}`), labelSize, 'bold', font, INK, maxTextW);
      const sub = str(it.text, '');
      const subText = sub ? fitText(sub, subSize, 'normal', font, INK, maxTextW) : null;

      const boxW = Math.max(labelText.width, subText ? subText.width : 0) + pad * 2;
      const boxH = labelText.height + (subText ? subText.height + 4 : 0) + pad * 2;
      return {
        index: i,
        pinX,
        pinY,
        side,
        labelText,
        subText,
        boxW,
        boxH,
        top: clamp(pinY - boxH / 2, 0, Math.max(0, height - boxH)),
      };
    });

    // Pass 2 — per side, stack boxes so they never overlap: sort by desired
    // top, push down to enforce the 8px gap, then a backward pass pulls the
    // stack back inside the region (best effort when the side is crowded).
    const GAP = 8;
    for (const side of ['left', 'right'] as const) {
      const stack = placed.filter((c) => c.side === side).sort((a, b) => a.top - b.top);
      let cursor = 0;
      for (const c of stack) {
        c.top = Math.max(c.top, cursor);
        cursor = c.top + c.boxH + GAP;
      }
      let limit = height;
      for (let i = stack.length - 1; i >= 0; i--) {
        const c = stack[i];
        c.top = Math.max(0, Math.min(c.top, limit - c.boxH));
        limit = c.top - GAP;
      }
    }

    // Invisible bounds rect keeps the group exactly width × height regardless
    // of where callouts land — the overlay aligns 1:1 with the art below it.
    const children: FabricObject[] = [
      new Rect({ left: 0, top: 0, width, height, fill: 'transparent', strokeWidth: 0 }),
    ];

    // Objects grouped by which FillValue they wear — filled in one applyFill
    // pass after layout (a solid string resolves to itself; a dead texture URL
    // falls back to the historic default).
    const pinObjs: FabricObject[] = [];
    const panelObjs: FabricObject[] = [];
    const labelTextObjs: FabricObject[] = [];
    const pinNumObjs: FabricObject[] = [];

    // Pass 3 — draw. Leader lines first, then boxes/text, pins on top.
    for (const c of placed) {
      const boxX = c.side === 'left' ? 0 : width - c.boxW;
      const innerX = c.side === 'left' ? c.boxW : width - c.boxW;
      const boxCY = c.top + c.boxH / 2;

      // Elbow leader: horizontal run from the box's inner edge to the pin's x,
      // then straight to the pin. Collapse to a single Line when the elbow
      // would be degenerate (pin nearly level with the box, or pin sitting
      // behind the box's inner edge).
      const runOK = c.side === 'left' ? c.pinX > innerX + 10 : c.pinX < innerX - 10;
      if (Math.abs(c.pinY - boxCY) < 6 || !runOK) {
        children.push(
          new Line([innerX, boxCY, c.pinX, c.pinY], {
            stroke: lineColor,
            strokeWidth: lineWidth,
            strokeLineCap: 'round',
          }),
        );
      } else {
        children.push(
          new Polyline(
            [
              { x: innerX, y: boxCY },
              { x: c.pinX, y: boxCY },
              { x: c.pinX, y: c.pinY },
            ],
            {
              fill: '',
              stroke: lineColor,
              strokeWidth: lineWidth,
              strokeLineCap: 'round',
              strokeLineJoin: 'round',
            },
          ),
        );
      }

      // Label box (skipped entirely when panelFill is "" — text sits bare).
      // An object FillValue is never '' so a gradient/texture keeps the box.
      if (panelFill !== '') {
        const box = new Rect({
          left: boxX,
          top: c.top,
          width: c.boxW,
          height: c.boxH,
          rx: 8,
          ry: 8,
          stroke: panelStroke || undefined,
          strokeWidth: panelStroke ? 2 : 0,
        });
        panelObjs.push(box);
        children.push(box);
      }
      c.labelText.set({ left: boxX + pad, top: c.top + pad });
      labelTextObjs.push(c.labelText);
      children.push(c.labelText);
      if (c.subText) {
        c.subText.set({ left: boxX + pad, top: c.top + pad + c.labelText.height + 4 });
        labelTextObjs.push(c.subText);
        children.push(c.subText);
      }

      // Pin: white-ringed circle, numbered 1..N when requested.
      const pin = new Circle({
        left: c.pinX,
        top: c.pinY,
        originX: 'center',
        originY: 'center',
        radius: dotR,
        stroke: '#ffffff',
        strokeWidth: ringW,
      });
      pinObjs.push(pin);
      children.push(pin);
      if (numbered) {
        const pinNum = new FabricText(String(c.index + 1), {
          left: c.pinX,
          top: c.pinY,
          originX: 'center',
          originY: 'center',
          fontSize: dotR * 1.15,
          fontFamily: font,
          fontWeight: 'bold',
        });
        pinNumObjs.push(pinNum);
        children.push(pinNum);
      }
    }

    // Resolve every fill once per object it lands on (a solid string → itself).
    await Promise.all([
      ...pinObjs.map((o) => applyFill(o, pinFill, '#e11d48')),
      ...panelObjs.map((o) => applyFill(o, panelFill, '#ffffff')),
      ...labelTextObjs.map((o) => applyFill(o, textFill, INK)),
      ...pinNumObjs.map((o) => applyFill(o, pinTextFill, '#ffffff')),
    ]);

    const group = new Group(children);
    group.set({ left: 0, top: 0 });
    return tagSmartObject(group, calloutLabels, params);
  },
};

registerRecipe(calloutLabels);
